import { GridColDef, DataGridPremiumProps } from '@mui/x-data-grid-premium';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { format as formatDate } from 'date-fns';
import CrmCell from './CrmCell';
import NameCell from './NameCell';
import ProviderNameCell from './ProviderNameCell';
import TagCell from './TagCell';
import AddressCell from './AddressCell';
import PhoneCell from './PhoneCell';
import StatusCell from './StatusCell';
import CrmLinkCell from './CrmLinkCell';
import CommandCell from './CommandCell';
import StreetAddressCell from './StreetAddressCell';
import ListNameCell from './ListNameCell';
import VolumeHeader from './VolumeHeader';
import TrendVolumeCell from './TrendVolumeCell';
import OverlayColumn from './OverlayColumn';
import SpecialityCell from './SpecialityCell';
import SiteOfCareTypeCell from './SiteOfCareTypeCell';
import { formatSocClassification } from 'src/components/Profiles/components/ProfileDetails/socTypes';
import TrendVolumeHeader from './TrendVolumeHeader';
import Header from './Header';
import StringCell from './StringCell';
import SecondaryAddressCell from './SecondaryAddressCell';
import ListItemsCell from './ListItemsCell';
import StandardCell from './StandardCell';
import EmailCell from './EmailCell';
import { Typography } from '../PulseUI';
import InstitutionCell from './InstitutionCell';
import StrategyListMembers from './StrategyListMembers';
import StrategyDateAdded from './StrategyDateAdded';
import StrategyDynamicColumn from './StrategyDynamicColumn';
import StrategyDynamicHeader from './StrategyDynamicHeader';
import SystemNameCell from './SystemNameCell';
import NumericalCell from './NumericalCell';
import NumericalHeader from './NumericalHeader';
import GPOCell from './GPOCell';
import EMRCell from './EMRCell';
import OperatingRoomCount from './OperatingRoomCount';
import StrategyProviderNameCell from './StrategyProviderNameCell';
import ListMemberships from './ListMemberships';

export { default as CrmSyncCell } from './CrmSyncCell';

/**
 * Context interface for ProviderNameCell to control navigation behavior
 */
export interface ProviderNameCellContext {
  shouldRouteToReferrals?: boolean;
}

/**
 * Enhanced providerName column function that accepts optional context
 * for conditional navigation behavior in specific table contexts
 *
 * @param field - Column field name
 * @param name - Column display name
 * @param context - Optional context to control navigation behavior
 * @returns GridColDef configuration for provider name column
 */
export const createProviderNameColumn = (
  field: string,
  name: string,
  context?: ProviderNameCellContext
): GridColDef => ({
  field,
  headerName: name,
  renderHeader: (params) => <Header {...params} />,
  renderCell: (params) => <ProviderNameCell {...params} context={context} />,
  width: 250,
  minWidth: 150,
});

export const TableColumn: {
  [key: string]: (
    field: string,
    name: string,
    displayOptions?: any
  ) => GridColDef;
} = {
  address: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    valueGetter: (_value, row) => row,
    valueFormatter: (value: any) =>
      [
        value.address_line_1,
        value.address_line_2,
        value.city,
        value.state,
        value.zip_code,
      ].join(', '),
    renderCell: (params) => <AddressCell {...params} />,
    flex: 1,
    minWidth: 150,
  }),
  affiliations: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    valueGetter: (_value, row) => row.hcp?.affiliations || row.affiliations || [],
    valueFormatter: (value: any) =>
      [...value]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ name }) => name)
        .join(', '),
    renderCell: (params) => <ListItemsCell {...params} field="affiliations" />,
    flex: 1,
    minWidth: 150,
  }),
  canonical: (field, name) => ({
    field,
    headerName: name,
    renderCell: (params) => <StandardCell {...params} field={field} />,
    valueGetter: (_value, row) => row?.is_canonical,
    align: 'center',
    headerAlign: 'center',
    flex: 0.1,
    minWidth: 100,
    sortable: true,
  }),
  city: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => {
      const primaryAddress =
        row.provider?.primary_address || row?.primary_address;
      return <Typography variant="body2">{primaryAddress?.city}</Typography>;
    },
    minWidth: 150,
    flex: 1,
  }),
  cityState: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => {
      const city = row?.city;
      const state = row?.state;
      const value = city && state ? `${city}, ${state}` : city || state || '';
      return <Typography variant="body2">{value}</Typography>;
    },
    minWidth: 150,
    flex: 1,
  }),
  crm_links: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    groupable: false,
    sortable: false,
    renderCell: (params) => <CrmCell {...params} />,
    width: 180,
    minWidth: 170,
  }),
  date: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    renderCell: ({ row }) => (
      <Typography variant="body2">
        {row[field] ? formatDate(new Date(row[field]), 'PPp') : ''}
      </Typography>
    ),
    minWidth: 150,
  }),
  dateAdded: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    renderCell: ({ row }) => (
      <Typography variant="body2">
        {row[field] ? formatDate(new Date(row[field]), 'PP') : ''}
      </Typography>
    ),
    minWidth: 150,
    flex: 1,
  }),
  email: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    renderCell: ({ row }) => <EmailCell row={row} />,
    minWidth: 150,
    flex: 1,
  }),
  emr: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    renderCell: (params) => <EMRCell {...params} field={field} />,
    flex: 1,
    minWidth: 150,
  }),
  focus: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => (
      <Typography variant="body2" className="truncate text-nowrap">
        {row?.focus || 'n/a'}
      </Typography>
    ),
    flex: 1,
    minWidth: 150,
  }),
  graduationYear: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => (
      <Typography variant="body2">{row?.graduation_year || 'n/a'}</Typography>
    ),
    flex: 1,
    minWidth: 150,
  }),
  groups: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    valueGetter: (_value, row) => row.hcp?.groups || [],
    valueFormatter: (value: any) =>
      [...value]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ name }) => name)
        .join(', '),
    renderCell: (params) => <ListItemsCell {...params} field="groups" />,
    flex: 1,
    minWidth: 120,
    maxWidth: 440,
  }),
  gpo: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    renderCell: (params) => <GPOCell {...params} field={field} />,
    flex: 1,
    minWidth: 120,
    maxWidth: 160,
  }),
  institution: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => <InstitutionCell row={row} />,
    flex: 1,
    minWidth: 150,
  }),
  isTeaching: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => (
      <Typography variant="body2" truncate>
        {row?.is_teaching_hospital ? 'Yes' : 'No'}
      </Typography>
    ),
    flex: 1,
    minWidth: 105,
  }),
  listMembership: (field, name) => ({
    headerName: name,
    field,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => <ListMemberships row={row} />,
    minWidth: 180,
    sortable: false,
  }),
  listName: (field, name) => ({
    headerName: name,
    field,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => <ListNameCell row={row} />,
    minWidth: 180,
    sortable: false,
  }),
  strategyProviderName: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <StrategyProviderNameCell {...params} />,
    width: 250,
    minWidth: 150,
  }),
  strategyDateAdded: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    minWidth: 120,
    flex: 1,
    valueGetter: (_value, row) => row.created_at,
    renderCell: ({ row }) => <StrategyDateAdded row={row} />,
  }),
  strategyListMembers: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    minWidth: 180,
    sortable: false,
    valueGetter: (_value, row) => row.name,
    renderCell: ({ row }) => <StrategyListMembers row={row} />,
  }),
  strategyDynamicColumn: (field, name, displayOptions) => {
    return {
      field,
      headerName: name,
      headerAlign: 'left',
      renderHeader: (params) => {
        if (displayOptions?.metricId !== undefined) {
          return (
            <StrategyDynamicHeader
              params={params}
              metricId={displayOptions.metricId}
              availableOptions={displayOptions.availableOptions}
            />
          );
        }
        return <Header {...params} />;
      },
      renderCell: (params) => (
        <StrategyDynamicColumn
          {...params}
          field={field}
          displayOptions={displayOptions}
        />
      ),
      width: 200,
      minWidth: 160,
      sortable: true, // Re-enable MUI's default sorting
    };
  },
  name: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <NameCell {...params} />,
    flex: 1,
    minWidth: 150,
    width: 300,
  }),
  staffedBeds: (field, name, isCurrency = false) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => (
      <NumericalCell row={row} field={field} isCurrency={isCurrency} />
    ),
    width: 105,
    flex: 0,
    sortable: true,
  }),
  operatingRooms: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => <OperatingRoomCount row={row} field={field} />,
    width: 135,
    flex: 0,
    sortable: true,
  }),
  patientRevenue: (field, name, isCurrency = false) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => (
      <NumericalCell row={row} field={field} isCurrency={isCurrency} />
    ),
    width: 130,
    flex: 0,
    sortable: true,
  }),
  currentAssets: (field, name, isCurrency = false) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => (
      <NumericalCell row={row} field={field} isCurrency={isCurrency} />
    ),
    width: 120,
    flex: 0,
    sortable: true,
  }),
  totalAssets: (field, name, isCurrency = false) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => (
      <NumericalCell row={row} field={field} isCurrency={isCurrency} />
    ),
    width: 105,
    flex: 0,
    sortable: true,
  }),
  currentLiability: (field, name, isCurrency = false) => ({
    headerName: name,
    renderHeader: (params) => <NumericalHeader {...params} />,
    field,
    align: 'right',
    renderCell: ({ row }) => (
      <NumericalCell row={row} field={field} isCurrency={isCurrency} />
    ),
    width: 130,
    flex: 0,
    sortable: true,
  }),
  numItems: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    flex: 1,
    valueGetter: (_value, row) => row.num_items,
    minWidth: 150,
    maxWidth: 200,
  }),
  overlayColumn: (_field, _name) => ({
    field: 'overlay',
    headerName: '',
    width: 0,
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    resizable: false,
    renderCell: (params) => <OverlayColumn {...params} />,
  }),
  owner: (field, name) => ({
    field,
    headerName: name,
    headerAlign: 'left',
    sortable: true,
    renderCell: (params) => <StandardCell {...params} field={field} />,
    valueGetter: (_value, row) => {
      const { owner } = row;
      return owner?.first_name && owner?.last_name
        ? `${owner?.first_name} ${owner?.last_name}`
        : owner?.username;
    },
    flex: 0.4,
  }),
  phone: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    renderCell: (params) => <PhoneCell {...params} />,
    flex: 1,
    minWidth: 150,
  }),
  providerName: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <ProviderNameCell {...params} />,
    valueGetter: (_value, row) => (row.provider ? row.provider.name : row.name),
    width: 250,
    minWidth: 150,
  }),
  systemName: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <SystemNameCell {...params} />,
    width: 300,
    minWidth: 150,
  }),
  secondaryAddresses: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    minWidth: 150,
    sortable: false,
    groupable: false,
    renderCell: (params) => <SecondaryAddressCell {...params} />,
    flex: 1,
  }),
  specialty: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <SpecialityCell {...params} />,
    valueGetter: (_value, row) => row?.specialty || row?.focus || 'n/a',
    minWidth: 150,
    flex: 1,
  }),
  siteOfCareType: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <SiteOfCareTypeCell {...params} />,
    valueGetter: (_value, row) =>
      formatSocClassification(row) || 'n/a',
    minWidth: 150,
    flex: 1,
  }),
  state: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => {
      const primaryAddress =
        row.provider?.primary_address || row?.primary_address;
      return <Typography variant="body2">{primaryAddress?.state}</Typography>;
    },
    minWidth: 125,
    width: 125,
    flex: 1,
  }),
  streetAddress: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    minWidth: 150,
    sortable: false,
    groupable: false,
    valueGetter: (_value, row) => row,
    renderCell: (params) => <StreetAddressCell {...params} />,
    flex: 1,
  }),
  string: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => {
      const value = row[field] || row.provider?.[field];
      return (
        <Typography variant="body2" truncate>
          {value || ''}
        </Typography>
      );
    },
    flex: 1,
    minWidth: 150,
  }),
  systems: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    sortable: false,
    groupable: false,
    valueGetter: (_value, row) => row.systems || [],
    valueFormatter: (value: any) =>
      [...value]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ name }) => name)
        .join(', '),
    renderCell: (params) => <ListItemsCell {...params} field="systems" />,
    flex: 1,
    minWidth: 150,
  }),
  tags: (field, name) => ({
    field,
    headerName: name,
    sortable: false,
    groupable: false,
    renderHeader: (params) => <Header {...params} />,
    renderCell: (params) => <TagCell {...params} />,
    flex: 1,
    minWidth: 150,
  }),
  territoryName: (field, name) => ({
    field,
    headerName: name,
    renderCell: (params) => <StandardCell {...params} field={field} />,
    valueGetter: (_value, row) => row?.name,
    flex: 0.4,
    maxWidth: 250,
    sortable: true,
  }),
  totalClaims: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    sortable: true,
    renderCell: ({ row }) => <StringCell field={field} row={row} />,
    valueGetter: (_value, row) => row.total_claims,
    align: 'right',
    headerAlign: 'right',
    flex: 0.25,
    minWidth: 150,
  }),
  totalPatients: (field, name) => ({
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    field,
    sortable: true,
    renderCell: ({ row }) => <StringCell field={field} row={row} />,
    valueGetter: (_value, row) => row.total_patients,
    align: 'right',
    headerAlign: 'right',
    flex: 0.25,
    minWidth: 150,
  }),
  trendVolume: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <TrendVolumeHeader {...params} />,
    groupable: false,
    sortable: false,
    headerClassName: 'MuiDataGrid-lightHeader',
    cellClassName: 'MuiDataGrid-lightCell',
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => <TrendVolumeCell {...params} field={field} />,
    type: 'string' as const,
    resizable: false,
    sortingOrder: ['desc', 'asc', null],
  }),

  zip: (field, name) => ({
    field,
    headerName: name,
    renderHeader: (params) => <Header {...params} />,
    renderCell: ({ row }) => {
      const value =
        row.primary_address?.zip_code ||
        row.provider?.primary_address?.zip_code;
      return (
        <Typography variant="body2" truncate>
          {value || ''}
        </Typography>
      );
    },
    sortable: false,
    minWidth: 90,
    width: 90,
    flex: 1,
  }),
};

export const TableSettings: Partial<DataGridPremiumProps> = {
  slots: {
    columnSortedAscendingIcon: () => <Icon icon={faChevronUp} size="sm" />,
    columnSortedDescendingIcon: () => <Icon icon={faChevronDown} size="sm" />,
  },
};

const TableCell = {
  Name: NameCell,
  ProviderName: ProviderNameCell,
  Tag: TagCell,
  Address: AddressCell,
  Phone: PhoneCell,
  Status: StatusCell,
  CrmLink: CrmLinkCell,
  Commands: CommandCell,
  VolumeHeader: VolumeHeader,
};

export { TableCell, OverlayColumn };
